import React, { useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useAuth } from '../../contexts/AuthContext';
import { useCollection } from '../../runtime/mobile/useCollection';
import { createAdminClient } from '../../runtime/mobile/adminClient';
import { readableMobileError } from '../../runtime/mobile/employeeRuntimeState';
import { colors, radius, spacing, typography } from '../../config/theme';
import { Card, ErrorBanner, Field, Loader, PrimaryButton, SectionLabel, TextButton, screen } from '../ui';

const PHASES = [
  { id: 'opening', label: 'Открытие' },
  { id: 'active', label: 'В течение смены' },
  { id: 'closing', label: 'Закрытие' },
];
// ASCII only — ids ride in an idempotency HTTP header (no Cyrillic allowed).
const slug = (text) => (text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/(^-|-$)/g, '').slice(0, 40) || 'x';

export default function ChecklistListScreen({ navigation }) {
  const { user } = useAuth();
  const checklists = useCollection('checklistTemplates');
  const taskTemplates = useCollection('taskTemplates');
  const locations = useCollection('locations');
  const client = useMemo(() => createAdminClient(user), [user]);

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', phase: 'opening', locationId: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const taskCount = useMemo(() => {
    const map = {};
    for (const t of taskTemplates.docs) if (t.active) map[t.checklistTemplateId] = (map[t.checklistTemplateId] || 0) + 1;
    return map;
  }, [taskTemplates.docs]);

  const submit = async () => {
    if (!form.name.trim() || !form.locationId) { setError('Укажите название и точку'); return; }
    setBusy(true);
    setError(null);
    try {
      const id = `chk-${slug(form.name)}-${Date.now().toString(36)}`;
      await client.createChecklist(id, { name: form.name.trim(), phase: form.phase, locationIds: [form.locationId], shiftTypeIds: [] });
      setCreating(false);
      setForm({ name: '', phase: 'opening', locationId: '' });
      navigation.navigate('ChecklistEditor', { id });
    } catch (e) {
      setError(readableMobileError(e));
    } finally {
      setBusy(false);
    }
  };

  if (checklists.loading && !checklists.docs.length) return <Loader />;

  return (
    <View style={screen.root}>
      <ScrollView contentContainerStyle={screen.content}>
        <ErrorBanner message={checklists.error} />
        {PHASES.map((phase) => {
          const list = checklists.docs.filter((c) => c.phase === phase.id);
          if (!list.length) return null;
          return (
            <View key={phase.id} style={{ gap: spacing.sm }}>
              <SectionLabel>{phase.label}</SectionLabel>
              {list.map((c) => (
                <Card key={c.id} onPress={() => navigation.navigate('ChecklistEditor', { id: c.id })} style={styles.row}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.name}>{c.name}</Text>
                    <Text style={styles.meta}>
                      {taskCount[c.id] || 0} задач · v{c.templateVersion}
                      {c.active ? '' : ' · отключён'}
                    </Text>
                  </View>
                  <Text style={styles.chevron}>›</Text>
                </Card>
              ))}
            </View>
          );
        })}
        {!checklists.docs.length && <Text style={styles.empty}>Пока нет ни одного чек-листа</Text>}
      </ScrollView>

      <Pressable style={styles.fab} onPress={() => setCreating(true)}>
        <Text style={styles.fabPlus}>+</Text>
      </Pressable>

      <Modal visible={creating} animationType="slide" transparent onRequestClose={() => setCreating(false)}>
        <View style={styles.sheetWrap}>
          <View style={styles.sheet}>
            <View style={styles.sheetHead}>
              <Text style={styles.sheetTitle}>Новый чек-лист</Text>
              <TextButton title="Отмена" kind="muted" onPress={() => setCreating(false)} />
            </View>
            <ErrorBanner message={error} />
            <Field label="Название" value={form.name} onChangeText={(name) => setForm((f) => ({ ...f, name }))} placeholder="Кофейня — открытие" />
            <Text style={styles.fieldLabel}>ФАЗА</Text>
            <View style={styles.segment}>
              {PHASES.map((p) => (
                <Pressable key={p.id} onPress={() => setForm((f) => ({ ...f, phase: p.id }))} style={[styles.seg, form.phase === p.id && styles.segOn]}>
                  <Text style={[styles.segText, form.phase === p.id && styles.segTextOn]}>{p.label}</Text>
                </Pressable>
              ))}
            </View>
            <Text style={styles.fieldLabel}>ТОЧКА</Text>
            <View style={styles.chipsWrap}>
              {locations.docs.filter((l) => l.active).map((l) => (
                <Pressable key={l.id} onPress={() => setForm((f) => ({ ...f, locationId: l.id }))} style={[styles.locChip, form.locationId === l.id && styles.locChipOn]}>
                  <Text style={[styles.locChipText, form.locationId === l.id && styles.locChipTextOn]}>{l.name}</Text>
                </Pressable>
              ))}
            </View>
            <PrimaryButton title="Создать и открыть" onPress={submit} loading={busy} />
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  name: { ...typography.h3, color: colors.textPrimary },
  meta: { ...typography.caption, color: colors.textMuted, marginTop: 1 },
  chevron: { fontSize: 22, color: colors.textMuted },
  empty: { ...typography.body, color: colors.textMuted, textAlign: 'center', paddingVertical: spacing.xxl },
  fab: { position: 'absolute', right: spacing.lg, bottom: spacing.lg, width: 56, height: 56, borderRadius: radius.md, backgroundColor: colors.primary, alignItems: 'center', justifyContent: 'center' },
  fabPlus: { fontSize: 30, color: colors.onPrimary, fontWeight: '700', marginTop: -2 },
  sheetWrap: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: colors.surface, borderTopLeftRadius: radius.lg, borderTopRightRadius: radius.lg, padding: spacing.lg, gap: spacing.md },
  sheetHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  sheetTitle: { ...typography.h2, color: colors.textPrimary },
  fieldLabel: { ...typography.caption, color: colors.textMuted, fontWeight: '700', letterSpacing: 1 },
  segment: { flexDirection: 'row', gap: spacing.xs, backgroundColor: colors.inputBackground, borderRadius: radius.sm, padding: 3 },
  seg: { flex: 1, paddingVertical: spacing.sm, alignItems: 'center', borderRadius: radius.sm - 3 },
  segOn: { backgroundColor: colors.surfaceElevated },
  segText: { ...typography.caption, color: colors.textSecondary, fontWeight: '700' },
  segTextOn: { color: colors.primary },
  chipsWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  locChip: { borderWidth: 1, borderColor: colors.border, borderRadius: radius.full, paddingHorizontal: spacing.md, paddingVertical: spacing.sm },
  locChipOn: { borderColor: colors.primary, backgroundColor: 'rgba(0,210,160,0.12)' },
  locChipText: { ...typography.caption, color: colors.textSecondary, fontWeight: '700' },
  locChipTextOn: { color: colors.textPrimary },
});
